import { ref, onMounted, watch } from "vue";
import moment from "moment";
import { getEncounterNotesListApi, getPatientMatchApi, getPatientVariablesApi } from "@/api/encounterNotes";
import { getEncounterSessionsApi } from "@/api/recordingApi";
import { mapPastNotes, normalizeNotesResponse, sortNotesByServiceDateDesc, getPatientById } from "../utils/encounterNotes";

export const useEncounterNoteInit = ({
  route,
  router,
  store,
  patientId,
  appointmentDate,
  appointmentType,
  encounterTitle,
  noteContent,
  contentVersion,
  saveStatus,
  isTitleManuallyEdited,
  signedAt,
  lastLoadedContent,
}) => {
  const patientRecord = ref(null);
  const pastNotes = ref([]);
  const recordings = ref([]);
  const hasRecording = ref(false);
  const patientVariables = ref([]);
  const patientMatchData = ref(null);
  const isLoadingPatient = ref(false);
  const isLoadingCalendar = ref(false);
  const isInitializing = ref(true);

  const fetchPatient = async () => {
    if (!patientId.value) {
      router.replace("/encounter-notes");
      return;
    }

    isLoadingPatient.value = true;
    try {
      patientRecord.value = await getPatientById(patientId.value, store);
    } catch (error) {
      console.error("Failed to load patient", error);
    } finally {
      isLoadingPatient.value = false;
    }
  };

  // Populate current note data from pastNotes array
  const populateCurrentNoteFromPastNotes = (notes) => {
    const noteId = route.params.noteId;
    
    if (!noteId) {
      // No noteId means invalid route, redirect to notes list
      router.replace("/encounter-notes");
      return;
    }
    
    // Find the current note in pastNotes by matching id
    const currentNote = notes.find(note => String(note.id) === String(noteId));
    
    if (currentNote) {
      if (currentNote.title) {
        encounterTitle.value = currentNote.title;
        // Prevent auto-updater from overwriting the loaded title
        isTitleManuallyEdited.value = true;
      }
      if (currentNote.service_date) {
        appointmentDate.value = moment(currentNote.service_date).format("YYYY-MM-DD");
      } else if (currentNote.signed_at) {
        // Signed note with no service_date: clear date so metabar hides it
        appointmentDate.value = null;
      }
      appointmentType.value = currentNote.appointment_type || appointmentType.value;
      noteContent.value = currentNote.content || "";
      // Track loaded content to detect user changes
      if (lastLoadedContent) {
        lastLoadedContent.value = currentNote.content || "";
      }
      contentVersion.value = currentNote.content_version || 1;
      if (signedAt) {
        signedAt.value = currentNote.signed_at || null;
      }
      // Note: hasRecording is now determined by fetchRecordings
    }
  };

  const fetchPastNotes = async () => {
    if (!patientId.value) return;

    try {
      const response = await getEncounterNotesListApi({ patient_id: patientId.value, sort: "created_at", order: "desc" });
      const notes = normalizeNotesResponse(response);
      const sortedNotes = sortNotesByServiceDateDesc(notes);
      
      // Populate current note data from the fetched notes before mapping
      populateCurrentNoteFromPastNotes(sortedNotes);
      
      pastNotes.value = mapPastNotes(sortedNotes);
    } catch (error) {
      console.error("Failed to fetch past notes", error);
      pastNotes.value = [];
    }
  };

  const fetchRecordings = async () => {
    const noteId = route.params.noteId;
    if (!noteId) return;

    try {
      const response = await getEncounterSessionsApi(noteId);
      const sessions = response?.sessions || [];
      recordings.value = sessions;
      hasRecording.value = sessions.length > 0;
    } catch (error) {
      console.error("Failed to fetch recording sessions:", error);
      recordings.value = [];
      hasRecording.value = false;
    }
  };

  const fetchPatientMatchData = async () => {
    if (!patientId.value) return;

    const [matchResult, variablesResult] = await Promise.allSettled([
      getPatientMatchApi(patientId.value),
      getPatientVariablesApi(patientId.value),
    ]);

    if (matchResult.status === "fulfilled") {
      patientMatchData.value = matchResult.value || null;
    } else {
      console.error("Failed to fetch patient match data:", matchResult.reason);
      patientMatchData.value = null;
    }

    if (variablesResult.status === "fulfilled") {
      const raw = variablesResult.value;
      // API returns { definitions: [{ key, display_label, ... }] }
      // Components expect [{ name, display_label, current_value }]
      const defs = raw?.definitions || raw?.patient_variables || [];
      patientVariables.value = defs.map(d => ({
        name: d.key || d.name,
        display_label: d.display_label,
        description: d.description,
        current_value: d.current_value,
      }));
    } else {
      console.error("Failed to fetch patient variables:", variablesResult.reason);
      patientVariables.value = [];
    }
  };

  const fetchPegboardList = async () => {
    if (!patientId.value) return;

    try {
      const userInfo = store.state.login?.userInfo;
      if (!userInfo) return;

      await store.dispatch("myPatients/patientInfo/getPegboardList", {
        customer_id: userInfo.customer_id,
        clinic_id: userInfo.clinic_id,
        patient_id: patientId.value,
      });

      // Fetch card data in parallel (same as MyPatient-demo)
      await Promise.all([
        store.dispatch("myPatients/patientInfo/fetchCurrentPatientVitals", patientId.value),
        store.dispatch("myPatients/patientInfo/fetchCurrentPatientMedications", patientId.value),
        store.dispatch("myPatients/patientInfo/fetchCurrentPatientAllergies", patientId.value),
        store.dispatch("myPatients/patientInfo/fetchCurrentPatientBillingOrders", patientId.value),
        store.dispatch("myPatients/patientInfo/fetchCurrentPatientDocuments", patientId.value),
      ]);
    } catch (error) {
      console.error("Error fetching pegboard data:", error);
    }
  };

  const resetForm = () => {
    appointmentDate.value = moment().format("YYYY-MM-DD");
    appointmentType.value = "Initial Consultation";
    encounterTitle.value = `Encounter Note - ${moment(appointmentDate.value).format("MM/DD/YY")}`;
    noteContent.value = "";
    if (lastLoadedContent) {
      lastLoadedContent.value = "";
    }
    contentVersion.value = 1;
    if (saveStatus) {
      saveStatus.value = "";
    }
    isTitleManuallyEdited.value = false;
  };

  const initPage = async () => {
    isInitializing.value = true;
    resetForm();
    // fetchPastNotes also populates the current note data (including hasRecording) from the list
    await Promise.all([fetchPatient(), fetchPastNotes(), fetchPegboardList()]);
    // Only fetch recordings after we know if the note has recordings
    await fetchRecordings();
    // Fetch patient match data (variables + orderable items) for unsigned (draft) notes
    if (!signedAt.value) {
      await fetchPatientMatchData();
    }
    isInitializing.value = false;
  };

  onMounted(() => {
    initPage();
  });

  watch(
    () => route.params,
    () => {
      initPage();
    },
    { deep: true }
  );

  return {
    patientRecord,
    pastNotes,
    recordings,
    hasRecording,
    patientVariables,
    patientMatchData,
    isLoadingPatient,
    isLoadingCalendar,
    isInitializing,
    initPage,
  };
};
